import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Backdrop,
  TableRow,
  TableHead,
  Table,
  TableContainer,
  TableCell,
  TableBody,
  Paper,
} from '@mui/material';
import { useSnackbar } from 'hooks/use-snackbar';
import { getProductsCsv } from 'api/catalog/get-products-csv';
import { importAsrsServerProductMarkingCsv } from 'api/catalog/import-asrs-server-product-marking-csv';
import { useFileState } from 'hooks/use-file-state';
import FileUploadArea from '../../components/FileUploadArea';

interface AsrsServerProductCsvDialogProps {
  open: boolean;
  onClose: () => void;
  serverId: string;
}

export const AsrsServerProductCsvDialog = ({ open, onClose, serverId }: AsrsServerProductCsvDialogProps) => {
  const { file, clearFile, handleFileChange } = useFileState();
  const [isLoading, setIsLoading] = useState(false);
  const snackbar = useSnackbar();

  const handleClose = () => {
    // Clear state when dialog is closed
    clearFile();
    setIsLoading(false);
    onClose();
  };

  const handleExport = () => {
    setIsLoading(true);
    getProductsCsv({
      success: () => {
        snackbar.show('CSV importado com sucesso', null, 'success');
        setIsLoading(false);
      },
      error: (err) => {
        snackbar.show('Falha ao importar CSV', null, 'error');
        setIsLoading(false);
      },
    });
  };

  const handleImport = () => {
    if (!file) return;

    setIsLoading(true);
    importAsrsServerProductMarkingCsv(serverId, file, {
      success: () => {
        snackbar.show('CSV exportar com sucesso', null, 'success');
        setIsLoading(false);
        handleClose();
      },
      error: (err) => {
        snackbar.show('Falha ao exportar CSV', null, 'error');
        setIsLoading(false);
      },
    });
  };

  return (
    <Dialog open={open} onClose={isLoading ? undefined : handleClose} fullWidth maxWidth="md">
      <Backdrop
        open={isLoading}
        sx={{
          position: 'absolute',
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <DialogTitle>Importar Produtos ASRS</DialogTitle>
      <DialogContent>
        <Typography variant="body1" paragraph>
          Para importar lista de produtos no sistema ASRS, siga os passos abaixo.
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', mb: 2, mt: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              Formato do CSV
            </Typography>
            <Typography variant="body2" paragraph sx={{ mb: 1 }}>
              O seu ficheiro CSV deve incluir apenas as seguintes colunas:
            </Typography>
            <Typography variant="body2" paragraph>
              - Defina <strong>active = 1</strong> para marcar um produto.
              <br />- Defina <strong>active = 0</strong> para desmarcar um produto.
            </Typography>
            <Button variant="outlined" onClick={handleExport} disabled={isLoading} sx={{ mb: 2 }}>
              Descarregar Catálogo
            </Button>
          </Box>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              display: 'inline-block',
              flexShrink: 0,
            }}
          >
            <TableContainer component={Paper} elevation={0}>
              <Table size="small" aria-label="Exemplo de formato CSV">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.800' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>sku</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>active</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontFamily: 'monospace' }}>"0002138732163123"</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>"1"</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontFamily: 'monospace' }}>"0096574653216312"</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>"0"</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                px: 1.5,
                py: 1,
                color: 'text.secondary',
                fontStyle: 'italic',
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              Exemplo de formato CSV
            </Typography>
          </Box>
        </Box>
        <Box sx={{ mb: 3 }}>
          <FileUploadArea
            file={file}
            onFileChange={handleFileChange}
            onClearFile={clearFile}
            accept=".csv"
            disabled={isLoading}
            title="Carregar ficheiro de importação"
            subtitle="Arraste o ficheiro CSV ou clique para selecionar"
            buttonText="Escolher ficheiro CSV"
            dragTitle="Solte o ficheiro CSV aqui"
            dragSubtitle="Solte para carregar"
            onError={(message) => snackbar.show(message, null, 'error')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancelar
        </Button>
        <Button onClick={handleImport} disabled={!file || isLoading} variant="contained" color="primary">
          {isLoading ? 'A processar...' : 'Importar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
